import React, { useEffect, useState } from "react";
import api from "@/api";
import { Plus, Edit, Trash2, MoreVertical, Building2, TrendingUp, RefreshCw } from "lucide-react";

function CompanyModal({ company, onClose, onSave }) {
  const isEdit = !!company?.id;
  const [form, setForm] = useState(company || { name: "", email: "", description: "", page_credits: 1000, ai_credits: 500, mistral_api_key: "", bulk_processing_enabled: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/admin/companies/${company.id}`, form);
      } else {
        await api.post("/admin/companies", form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{isEdit ? "Edit Company" : "New Company"}</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          {[
            { label: "Company Name", key: "name", type: "text", required: true, placeholder: "Acme Corp" },
            { label: "Email", key: "email", type: "email", placeholder: "admin@acme.com" },
            { label: "Description", key: "description", type: "text", placeholder: "Brief description" },
            { label: "Page Credits", key: "page_credits", type: "number", placeholder: "1000" },
            { label: "AI Credits", key: "ai_credits", type: "number", placeholder: "500" },
            { label: "Mistral API Key (override)", key: "mistral_api_key", type: "password", placeholder: "sk-... (optional)" },
          ].map(({ label, key, type, required, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
              <input
                data-testid={`company-${key}-input`}
                type={type}
                value={form[key] ?? ""}
                onChange={(e) => setForm(f => ({ ...f, [key]: type === "number" ? parseInt(e.target.value) || 0 : e.target.value }))}
                required={required}
                placeholder={placeholder}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              />
            </div>
          ))}
          {/* Bulk Processing Toggle */}
          <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md bg-slate-50">
            <div>
              <p className="text-sm font-medium text-slate-900">Bulk Processing</p>
              <p className="text-xs text-slate-500">Allow this company to upload and process batches of documents</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                data-testid="company-bulk-processing-toggle"
                type="checkbox"
                checked={form.bulk_processing_enabled || false}
                onChange={e => setForm(f => ({ ...f, bulk_processing_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#006080]" />
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="save-company-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TopupModal({ company, onClose, onSave }) {
  const [form, setForm] = useState({
    page_credits: 0,
    ai_credits: 0,
    remarks: "",
    invoice_number: "",
    topup_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/companies/${company.id}/topup`, form);
      onSave();
    } catch (e) {} finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-sm animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">Top Up Credits — {company.name}</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Page Credits</label>
              <input data-testid="topup-page-credits" type="number" value={form.page_credits} onChange={e => setForm(f => ({...f, page_credits: parseInt(e.target.value)||0}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">AI Credits</label>
              <input data-testid="topup-ai-credits" type="number" value={form.ai_credits} onChange={e => setForm(f => ({...f, ai_credits: parseInt(e.target.value)||0}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Invoice / Credit Note #</label>
            <input type="text" value={form.invoice_number} onChange={e => setForm(f => ({...f, invoice_number: e.target.value}))} placeholder="INV-2024-001" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Date</label>
            <input type="date" value={form.topup_date} onChange={e => setForm(f => ({...f, topup_date: e.target.value}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Remarks</label>
            <input type="text" value={form.remarks} onChange={e => setForm(f => ({...f, remarks: e.target.value}))} placeholder="e.g. Monthly renewal" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="topup-save-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : "Apply Top-Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RecurringModal({ company, onClose, onSave }) {
  const existing = company?.recurring_credits || {};
  const [form, setForm] = useState({
    is_active: existing.is_active || false,
    page_credits: existing.page_credits || 0,
    ai_credits: existing.ai_credits || 0,
    day_of_month: existing.day_of_month || 1,
    rollover_unused: existing.rollover_unused || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/companies/${company.id}/recurring`, form);
      onSave();
    } catch (err) {} finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <RefreshCw size={14} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900">Recurring Credits — {company.name}</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
            <div>
              <p className="text-sm font-medium text-slate-900">Enable Recurring Top-Up</p>
              <p className="text-xs text-slate-500">Auto-apply credits on the set day each month</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({...f, is_active: e.target.checked}))} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#006080]" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Page Credits / Month</label>
              <input data-testid="recurring-page-credits" type="number" min={0} value={form.page_credits} onChange={e => setForm(f => ({...f, page_credits: parseInt(e.target.value)||0}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">AI Credits / Month</label>
              <input data-testid="recurring-ai-credits" type="number" min={0} value={form.ai_credits} onChange={e => setForm(f => ({...f, ai_credits: parseInt(e.target.value)||0}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Anniversary Day of Month (1–28)</label>
            <input data-testid="recurring-day" type="number" min={1} max={28} value={form.day_of_month} onChange={e => setForm(f => ({...f, day_of_month: Math.min(28, Math.max(1, parseInt(e.target.value)||1))}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
            <div>
              <p className="text-sm font-medium text-slate-900">Rollover Unused Credits</p>
              <p className="text-xs text-slate-500">If off, unused credits are lost each month</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.rollover_unused} onChange={e => setForm(f => ({...f, rollover_unused: e.target.checked}))} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#006080]" />
            </label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="save-recurring-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { type: 'create'|'edit'|'topup', company? }

  const fetchCompanies = () => {
    setLoading(true);
    api.get("/admin/companies").then(r => setCompanies(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchCompanies(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this company?")) return;
    await api.delete(`/admin/companies/${id}`);
    fetchCompanies();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Companies</h2>
          <p className="text-sm text-slate-500">Manage all company workspaces on the platform.</p>
        </div>
        <button
          onClick={() => setModal({ type: "create" })}
          data-testid="create-company-button"
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
        >
          <Plus size={14} /> New Company
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Company", "Users", "Documents", "Page Credits", "AI Credits", "Status", ""].map(h => (
                  <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {companies.map((co, i) => (
                <tr key={co.id} data-testid={`company-row-${co.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${i%2===1?"bg-slate-50/50":""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-[#E5F0F2] flex items-center justify-center text-[#006080] text-xs font-bold">
                        {co.name[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{co.name}</p>
                        {co.email && <p className="text-xs text-slate-400">{co.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{co.user_count || 0}</td>
                  <td className="px-4 py-3 text-slate-600">{co.document_count || 0}</td>
                  <td className="px-4 py-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#006080] rounded-full" style={{width:`${Math.min(100,((co.page_credits-co.page_credits_used)/Math.max(1,co.page_credits))*100)}%`}} />
                        </div>
                        <span className="text-xs text-slate-500">{co.page_credits - co.page_credits_used}/{co.page_credits}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#006080] rounded-full" style={{width:`${Math.min(100,((co.ai_credits-co.ai_credits_used)/Math.max(1,co.ai_credits))*100)}%`}} />
                      </div>
                      <span className="text-xs text-slate-500">{co.ai_credits - co.ai_credits_used}/{co.ai_credits}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${co.is_active?"bg-[#E5F0F2] text-[#006080]":"bg-slate-100 text-slate-400"}`}>
                      {co.is_active ? "Active" : "Archived"}
                    </span>
                    {co.bulk_processing_enabled && (
                      <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">Bulk</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setModal({type:"edit",company:co})} data-testid={`edit-company-${co.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"><Edit size={13} /></button>
                      <button onClick={() => setModal({type:"topup",company:co})} data-testid={`topup-company-${co.id}`} className="p-1.5 hover:bg-[#E5F0F2] rounded text-slate-400 hover:text-[#006080] transition-colors"><TrendingUp size={13} /></button>
                      <button onClick={() => setModal({type:"recurring",company:co})} data-testid={`recurring-company-${co.id}`} title="Recurring Credits" className="p-1.5 hover:bg-purple-50 rounded text-slate-400 hover:text-purple-600 transition-colors"><RefreshCw size={13} /></button>
                      <button onClick={() => handleDelete(co.id)} data-testid={`delete-company-${co.id}`} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {companies.length === 0 && (
            <div className="text-center py-12"><Building2 size={28} className="text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No companies yet</p></div>
          )}
        </div>
      )}

      {modal?.type === "create" && <CompanyModal onClose={() => setModal(null)} onSave={() => { setModal(null); fetchCompanies(); }} />}
      {modal?.type === "edit" && <CompanyModal company={modal.company} onClose={() => setModal(null)} onSave={() => { setModal(null); fetchCompanies(); }} />}
      {modal?.type === "topup" && <TopupModal company={modal.company} onClose={() => setModal(null)} onSave={() => { setModal(null); fetchCompanies(); }} />}
      {modal?.type === "recurring" && <RecurringModal company={modal.company} onClose={() => setModal(null)} onSave={() => { setModal(null); fetchCompanies(); }} />}
    </div>
  );
}
